import React from 'react';
import {Box, TextField, Typography, FormControlLabel, Switch, Stack} from '@mui/material';
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import {LongTextQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface LongTextInputProps {
  question: LongTextQuestion;
  onQuestionUpdate: (question: LongTextQuestion) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const LongTextInput: React.FC<LongTextInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      title: e.target.value
    });
  };

  const handleDescriptionChange = (value?: string) => {
    onQuestionUpdate({
      ...question,
      description: value || ''
    });
  };

  const handlePlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      placeholder: e.target.value
    });
  };

  const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQuestionUpdate({
      ...question,
      required: e.target.checked
    });
  };

  return (
    <Stack
      onClick={onSelect}
      sx={{
        p: 3,
        borderRadius: 1,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: 'primary.light',
          backgroundColor: 'action.hover'
        },
        position: 'relative'
      }}
    >
      <Box sx={{alignSelf: 'flex-end'}}>
        <FormControlLabel
          control={
            <Switch
              checked={question.required || false}
              onChange={handleRequiredChange}
              onClick={(e) => e.stopPropagation()}
              size="small"
            />
          }
          label={<Typography variant="caption">{t('Required')}</Typography>}
          labelPlacement="start"
          onClick={(e) => e.stopPropagation()}
          sx={{m: 0}}
        />
      </Box>

      <TextField
        fullWidth
        label={t('Question Label')}
        value={question.title}
        onChange={handleTitleChange}
        variant="standard"
        sx={{mb: 2, pr: 12}}
        onClick={(e) => e.stopPropagation()}
      />

      {isSelected ? (
        <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
          <MdEditor
            value={question.description || ''}
            onChange={handleDescriptionChange}
            placeholder={t('Question Description (optional)')}
            className={'mini-mdx'}
          />
        </Box>
      ) : (
        question.description && (
          <Box sx={{mb: 2}}>
            <MdRenderer>
              {question.description}
            </MdRenderer>
          </Box>
        )
      )}

      <TextField
        fullWidth
        placeholder={question.placeholder || t('Type your answer here...')}
        disabled
        variant="outlined"
        multiline
        rows={4}
        sx={{
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)'
          }
        }}
      />

      {isSelected && (
        <Box sx={{mt: 2}}>
          <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
            {t('Placeholder Text')}
          </Typography>
          <TextField
            fullWidth
            value={question.placeholder || ''}
            onChange={handlePlaceholderChange}
            variant="outlined"
            size="small"
            placeholder={t('Enter placeholder text...')}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      )}
    </Stack>
  );
};

export default LongTextInput;
